import { useAsyncData } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { ArrowRight } from 'lucide-react'
import { memo } from 'react'
import { IoSparklesSharp } from 'react-icons/io5'

import { AssistantMessage } from '@/components/assistant/messages/AssistantMessage'
import { AssistantTask, TaskLoader } from '@/components/assistant/messages/AssistantTask'
import { storeActions } from '@/store/actions'
import { AssistantFeedItem } from '@/store/assistant'
import { getCurrentBeneficiary } from '@/store/helpers'
import { useStoreDispatch, useStoreState } from '@/store/hooks'
import { Task } from '@/store/task'

export const TasksHandler = memo(function ({
  feed,
  generatedTasks,
  isThinking,
}: {
  feed: AssistantFeedItem[]
  generatedTasks: Task[]
  isThinking: boolean
}) {
  const {
    data: [beneficiary],
  } = useAsyncData([getCurrentBeneficiary])

  const generationId = useStoreState((state) => state.assistant.generationId)
  const previouslyGeneratedTasks =
    feed.filter((item) => item.type === 'tasks').find((item) => item.generationId === generationId)
      ?.tasks ?? []

  if (!beneficiary) return null
  return (
    <AssistantMessage>
      <div className="flex flex-col gap-4">
        {generatedTasks.map((task) => {
          if (!task.title) return null
          return <AssistantTask key={task.id} task={task} />
        })}
      </div>

      {isThinking && <TaskLoader className="mt-4" />}

      {generationId && (
        <GenerateMoreTasksButton
          previouslyGeneratedTasks={previouslyGeneratedTasks}
          generationId={generationId}
          isDisabled={isThinking}
          className="mt-4"
        />
      )}
    </AssistantMessage>
  )
})

export function GenerateMoreTasksButton({
  className,
  generationId,
  isDisabled,
  previouslyGeneratedTasks,
}: {
  className?: string
  generationId: string
  isDisabled: boolean
  previouslyGeneratedTasks: Task[]
}) {
  const dispatch = useStoreDispatch()
  const { t } = useT({ keyPrefix: 'assistant' })
  const context = useStoreState((state) => state.assistant.tempData?.content)

  const handleGenerateMoreTasks = () => {
    if (isDisabled) return
    dispatch(
      storeActions.assistant.generate.beneficiary.moreTasks({
        generationId,
        context: context || '',
        generatedTasks: previouslyGeneratedTasks,
      }),
    )
  }

  if (isDisabled) return null
  return (
    <div className={className}>
      <div
        onClick={handleGenerateMoreTasks}
        className="text-primary-200 hover:text-primary-foreground flex w-fit cursor-pointer items-center gap-2 underline transition-colors"
      >
        <ArrowRight className="size-5" />
        {t('generateMoreTasks')}
        <IoSparklesSharp className="size-5" />
      </div>
    </div>
  )
}
